'use client';
import React, { useState, useEffect } from 'react';
import { Stack, Box, Typography } from '@mui/material';
import { ComponentSharedCountdownTimer } from '@peerless/data-access/strapi';
import { getColorConversion } from '../utils';
import { withSectionLayout } from './SectionLayout';
import { useTranslations } from 'next-intl';
import { differenceInSeconds, parseISO } from 'date-fns';
import { SectionHeader } from './SectionHeader';

interface Props {
  data: ComponentSharedCountdownTimer;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer: React.FC<Props> = ({ data }) => {
  const { date, timeValueBackground, labelColor } = data;
  const t = useTranslations('Countdown');

  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (!date) return;

    const targetDate = parseISO(date);

    const updateCountdown = () => {
      const now = new Date();
      const totalSeconds = differenceInSeconds(targetDate, now);

      if (totalSeconds <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      setTimeLeft({ days, hours, minutes, seconds });
    };

    // Initial update
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [date]);

  const bgColor = timeValueBackground ? getColorConversion(timeValueBackground) : '#EE3124';

  const textColor = labelColor ? getColorConversion(labelColor) : '#333';

  const timeBlocks = [
    { value: timeLeft.days, label: t('days') },
    { value: timeLeft.hours, label: t('hours') },
    { value: timeLeft.minutes, label: t('minutes') },
    { value: timeLeft.seconds, label: t('seconds') },
  ];

  return (
    <Stack
      sx={{
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SectionHeader {...data.sectionHeader} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: { xs: 1, sm: 2, md: 3 },
          flexWrap: 'wrap',
        }}
      >
        {timeBlocks.map(block => (
          <Box
            key={block.label}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                backgroundColor: bgColor,
                color: timeValueBackground === 'red' ? '#fff' : '#EE3124',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 'bold',
                padding: { xs: '12px 16px', sm: '16px 24px', md: '20px 30px' },
                minWidth: { xs: '60px', sm: '75px', md: '90px' },
                textAlign: 'center',
              }}
            >
              {block.value}
            </Box>
            <Typography
              sx={{
                marginTop: '10px',
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                color: textColor,
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {block.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

export const CountdownTimerSection = withSectionLayout(CountdownTimer);
